import _ from "lodash";

export const metaSchema = {
    id: "http://restspace.io/field-spec",
    definitions: {
        "fields": {
            type: "array",
            items: {
                type: [ "object", "array" ],
                oneOf: [
                    {
                        title: "single field",
                        type: "object",
                        properties: {
                            name: { type: "string" },
                            field: {
                                type: "string",
                                enum: [ "text", "textarea", "html", "number", "currency", "checkbox", "date", "date-time", "dropdown", "upload", "hidden" ],
                            },
                            required: { type: "boolean" }
                        },
                        if: {
                            type: "object",
                            properties: {
                                field: { type: "string", const: "dropdown" }
                            },
                            required: [ "field" ]
                        },
                        then: {
                            type: "object",
                            properties: {
                                options: { type: "string", description: "Separate items with |" }
                            }
                        },
                        required: [ 'name', 'field' ]
                    },
                    {
                        title: "list",
                        type: "object",
                        properties: {
                            name: { type: "string" },
                            fields: {
                                "$ref": "#/definitions/fields"
                            }
                        },
                        required: [ 'name', 'fields' ]
                    }
                ],
                editor: "oneOfRadio"
            }
        }
    },
    type: "object",
    properties: {
        "title": { type: "string" },
        "pathPattern": { type: "string" },
        fields: {
            "$ref": "#/definitions/fields"
        }
    }
}

const getFieldsFromProperties = (schema: object) =>
    Object.entries(schema['properties'])
        .map(([key, value]) =>
            Object.assign(
                {
                    name: key,
                    required: schema['required'] && (schema['required'] as string[]).includes(key)
                },
                toFieldSpec(value as object)
            )
        );

export const toFieldSpec = (schema: object) => {
    const fieldSpec: object = {};
    if (schema['title']) fieldSpec['title'] = schema['title'];
    if (schema['pathPattern']) fieldSpec['pathPattern'] = schema['pathPattern'];

    if (schema['type'] === 'object') {
        fieldSpec['fields'] = getFieldsFromProperties(schema);
    } else if (schema['type'] === 'array') {
        if (!(schema['items'] && schema['items']['type'] == 'object')) {
            throw new Error("can't represent array of non-objects");
        }
        fieldSpec['fields'] = getFieldsFromProperties(schema['items']);
    } else {
        switch (schema['type']) {
            case 'number':
                if (schema['format'] === 'currency' || schema['editor'] === 'currency') {
                    fieldSpec['field'] = 'currency';
                } else {
                    fieldSpec['field'] = 'number';
                }
                break;
            case 'boolean':
                fieldSpec['field'] = 'checkbox';
                break;
            case 'string':
                if (schema['enum']) {
                    fieldSpec['field'] = 'dropdown';
                    if (schema['enum'].length) fieldSpec['options'] = schema['enum'].join('|');
                } else if (schema['editor']) {
                    switch (schema['editor']) {
                        case 'html':
                            fieldSpec['field'] = 'html';
                            break;
                        case 'upload':
                            fieldSpec['field'] = 'upload';
                            break;
                    }
                } else if (schema['format']) {
                    if (schema['format'] === 'date') {
                        fieldSpec['field'] = 'date';
                    } else if (schema['format'] = 'date-time') {
                        fieldSpec['field'] = 'date-time';
                    }
                } else if (schema['hidden']) {
                    fieldSpec['field'] = 'hidden';
                } else {
                    fieldSpec['field'] = 'text';
                }
                break;
        }
    }
    return fieldSpec;
}

export const toSchema = (fieldSpec: object) => {
    let schema: object = {};
    if (fieldSpec['title']) schema['title'] = fieldSpec['title'];
    if (fieldSpec['pathPattern']) schema['pathPattern'] = fieldSpec['pathPattern'];

    if (fieldSpec['fields']) {
        const fields: object[] = fieldSpec['fields'];
        schema['type'] = 'object';
        schema['properties'] = {};
        const required: string[] = [];
        fields.forEach(field => {
            schema['properties'][field['name']] = toSchema(field);
            if (field['required']) required.push(field['name']);
        });
        if (required.length) schema['required'] = required;
        if (fieldSpec['name']) { // it's an array of objects
            schema = {
                type: 'array',
                items: schema
            }
        }
    } else {
        let subSchema = {};
        switch (fieldSpec['field']) {
            case 'text':
                subSchema = { type: 'string' };
                break;
            case 'textarea':
                subSchema = { type: 'string', editor: 'textarea' };
                break;
            case 'html':
                subSchema = { type: 'string', editor: 'html' };
                break;
            case 'number':
                subSchema = { type: 'number' };
                break;
            case 'currency':
                subSchema = { type: 'number', editor: 'currency' }
                break;
            case 'checkbox':
                subSchema = { type: 'boolean' };
                break;
            case 'date':
                subSchema = { type: 'string', format: 'date' };
                break;
            case 'date-time':
                subSchema = { type: 'string', format: 'date-time' };
                break;
            case 'dropdown':
                subSchema = { type: 'string', enum: fieldSpec['options'].split('|') };
                break;
            case 'upload':
                subSchema = { type: 'string', editor: 'upload' };
                break;
            case 'hidden':
                subSchema = { type: 'string', hidden: true };
                break;
        }
        Object.assign(schema, subSchema);
    }
    return schema;
}

export const representableAsFieldSpec = (schema0: object): boolean => {
    let fieldSpec: object = {};
    try {
        fieldSpec = toFieldSpec(schema0);
    } catch { }
    const schema1 = toSchema(fieldSpec);
    return _.isEqual(schema0, schema1);
}